import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronRight, Calculator as CalcIcon, ExternalLink } from "lucide-react";
import { api, fmtDate, BACKEND_URL, imgUrl } from "@/lib/api";
import { AuthorBadge } from "@/components/site/AuthorBadge";
import { NewsletterBox } from "@/components/site/NewsletterBox";
import { Seo, faqSchema } from "@/components/site/Seo";

export default function Article() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null);
    api.get(`/public/articles/${slug}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [slug]);

  if (error) return <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="article-not-found"><h1 className="font-display text-3xl font-bold">Articolo non trovato</h1><Link to="/blog" className="btn-ghost mt-6">Torna al blog</Link></div>;
  if (!data) return <div className="mx-auto max-w-3xl px-4 py-24 text-muted-foreground">Caricamento...</div>;

  const { article: a, related } = data;
  const schema = [
    { "@context": "https://schema.org", "@type": "Article", headline: a.title, description: a.meta_description || a.excerpt, image: a.cover_image,
      datePublished: a.published_at, author: { "@type": "Person", name: a.author, url: "https://polizzapet.it/chi-siamo" },
      publisher: { "@type": "Organization", name: "Polizzapet.it" }, mainEntityOfPage: `https://polizzapet.it/blog/${a.slug}` },
  ];
  if (a.faq?.length) schema.push(faqSchema(a.faq));

  return (
    <article className="mx-auto max-w-7xl px-4 lg:px-8 py-10" data-testid="article-page">
      <Seo title={a.meta_title || a.title} description={a.meta_description || a.excerpt} schema={schema} />
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-6" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-foreground">Home</Link><ChevronRight size={12} /><Link to="/blog" className="hover:text-foreground">Blog</Link><ChevronRight size={12} /><span className="capitalize">{a.category}</span>
      </nav>
      <div className="grid gap-12 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0">
          <p className="label-mono mb-3">{a.category} · {a.pet_type} · {a.reading_time} min di lettura</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]" data-testid="article-title">{a.title}</h1>
          <p className="text-base md:text-lg text-muted-foreground mt-4">{a.excerpt}</p>
          <div className="mt-6"><AuthorBadge compact date={fmtDate(a.published_at)} /></div>
          {a.cover_image && <img src={imgUrl(a.cover_image)} alt={a.cover_alt || a.title} className="mt-8 rounded-2xl w-full h-72 sm:h-96 object-cover" />}
          <div className="prose-pet mt-8" data-testid="article-content"><ReactMarkdown remarkPlugins={[remarkGfm]}>{a.content}</ReactMarkdown></div>

          <div className="mt-12 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-7 flex flex-col sm:flex-row items-start sm:items-center gap-5" data-testid="article-calculator-callout">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-emerald-500 text-white"><CalcIcon /></span>
            <div className="flex-1">
              <p className="font-display text-lg font-semibold">Quanto rischi di spendere per il tuo {a.pet_type === "gatto" ? "gatto" : "cane"}?</p>
              <p className="text-sm text-muted-foreground">Calcolo gratuito in 30 secondi con i costi della tua città.</p>
            </div>
            <Link to="/preventivo" className="btn-primary" data-testid="article-cta-calcolatore">Calcola ora</Link>
          </div>

          <div className="mt-4 surface p-5 border-emerald-500/40" data-testid="article-affiliate-box">
            <p className="font-semibold">Barkibu: −25€ con il codice <span className="font-mono text-amber-700 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5 select-all" data-testid="article-promo-code">POLIZZAPET10</span></p>
            <p className="text-xs text-muted-foreground mt-1">Inseriscilo nel campo "Hai un codice sconto?" al pagamento. Link di affiliazione · nessun costo aggiuntivo per te.</p>
            <a href={`${BACKEND_URL}/api/go/barkibu?article=${a.slug}`} onClick={() => navigator.clipboard?.writeText("POLIZZAPET10").catch(() => {})} target="_blank" rel="noopener noreferrer sponsored" className="btn-primary !py-2.5 text-sm mt-3 inline-flex" data-testid="article-affiliate-link">
              Risparmia 25€ con il codice POLIZZAPET10 <ExternalLink size={14} />
            </a>
          </div>

          {a.faq?.length > 0 && (
            <section className="mt-14" data-testid="article-faq">
              <h2 className="font-display text-2xl font-bold mb-5">Domande frequenti</h2>
              <div className="divide-y divide-border surface">
                {a.faq.map((f) => <div key={f.q} className="p-5"><p className="font-semibold">{f.q}</p><p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.a}</p></div>)}
              </div>
            </section>
          )}
          <div className="mt-14"><AuthorBadge /></div>
        </div>
        <aside className="space-y-6 lg:sticky lg:top-24 h-fit">
          <NewsletterBox compact />
          {related?.length > 0 && (
            <div className="surface p-5" data-testid="related-articles">
              <p className="label-mono mb-4">Leggi anche</p>
              <ul className="space-y-4">
                {related.map((r) => <li key={r.id}><Link to={`/blog/${r.slug}`} className="font-medium text-sm leading-snug hover:text-emerald-500 transition-colors">{r.title}</Link><p className="text-xs text-muted-foreground mt-1">{r.reading_time} min</p></li>)}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </article>
  );
}
